/**
 * The one place a smart query turns into card rows.
 *
 * utils/smartSearch parses and matches in memory; cardData talks to Postgres.
 * This is the strategy between them — which query to try, and what to do when
 * it comes back empty — and it lives on its own so the Explore-tab menu and
 * the scanner's manual search cannot drift apart, and so the All tab, the wish
 * list and Trade Now can pick it up unchanged.
 *
 * It also decides WHICH query the sort, the filter and the facets all describe,
 * which is the reason it returns a `match` alongside the rows. The tiers below
 * mean the predicate that finally answered is often not the one the person
 * typed: "evolv char" ends up as "a card called char, inside Evolutions". A
 * filter sheet that offered the rarities of the literal query while the grid
 * showed the results of tier two would be describing a search nobody ran.
 */
import * as cardData from './cardData';
import { matchSets, residualWords, rankCards } from '../utils/smartSearch';
import { SEARCH_RELEVANCE_SORT, hasCatalogFilters } from './cardData/catalogFilters';

/**
 * Every predicate worth trying, best answer first.
 *
 * Tiers, in order, stopping at the first that matches anything. For an ordinary
 * query only the first one is ever reached.
 *
 *   1. everything the person typed — name words AND card number
 *   2. "evolutions 11": a set explains some of the words, so look inside that
 *      set for whatever is left over
 *   3. "swsh066": the letters are a set-code prefix stuck to the number, not
 *      part of any card's name — drop them and trust the number
 *   4. the number was a red herring, so trust the name
 *
 * @param {object} parsed from utils/smartSearch.parseQuery
 * @param {object[]} sets the catalogue set list, for tier 2
 * @returns {Array<{ words: string[], localIds: string[], setIds: string[]|null }>}
 */
function matchTiers(parsed, sets) {
  const words = parsed?.words || [];
  const localIds = parsed?.localIds || [];
  const tiers = [{ words, localIds, setIds: null }];

  if (words.length) {
    const setHits = matchSets(parsed, sets || []);
    const residual = residualWords(parsed, setHits);
    if (setHits.length && residual.length < words.length) {
      tiers.push({ words: residual, localIds, setIds: setHits.map((s) => s.id) });
    }
  }

  if (localIds.length && words.length) {
    // "swsh066" is a printed card number, so the prefixed forms are tried on
    // their own first: the bare 66 matches 140 cards across the catalogue and
    // none of them is the one that was asked for.
    const prefixed = localIds.filter((id) => /^[A-Z]+\d/.test(id));
    if (prefixed.length) tiers.push({ words: [], localIds: prefixed, setIds: null });
    tiers.push({ words: [], localIds, setIds: null });
    tiers.push({ words, localIds: [], setIds: null });
  }

  return tiers;
}

/**
 * Card rows for a parsed query, plus the predicate that answered and what that
 * predicate's whole result set contains.
 *
 * WHICH TIER WINS IS DECIDED UNFILTERED, and that is the load-bearing decision
 * in this file. A tier used to be rejected because its rows came back empty.
 * Once a filter can narrow those rows, "empty" stops meaning "this tier does
 * not match" and starts meaning "you ticked Secret Rare" — so ticking a chip
 * would silently drop the search down to tier three and answer a different
 * question. The facet read is unfiltered by design, so it is what decides the
 * tier, and the filter then narrows rows within the tier it chose. An empty
 * grid under a filter is an honest empty grid.
 *
 * Cost, for the common case: two requests in parallel — the rows and the
 * facets — plus the image lookup the rows already needed. The facet read is six
 * narrow columns with no ordering and no images. A sort or a filter change
 * re-runs the rows only; the facets are keyed on the query, which the sort and
 * the filter do not change.
 *
 * @param {object} parsed  from utils/smartSearch.parseQuery
 * @param {{
 *   limit?: number, sets?: object[]|null,
 *   sort?: string|null, filters?: object|null, facets?: boolean,
 *   match?: object|null,
 * }} [options]
 *   `sets` is the catalogue set list if the caller already holds it. Omit it
 *   and the list is fetched only if a fallback tier actually needs it, from
 *   cardData's cache.
 *
 *   `match` is a tier this query has already resolved. Changing the sort or the
 *   filter does not change which tier answers - only the query does - so a
 *   caller that still holds the tier hands it back and the whole probe, the
 *   facet read and the set-list fetch are all skipped. That is what makes
 *   re-sorting a search one request instead of three.
 * @returns {Promise<{ rows: object[], match: object|null, facets: object|null }>}
 */
export async function searchCatalog(parsed, {
  limit = 60, sets = null, sort = null, filters = null, facets = true,
  match: knownMatch = null,
} = {}) {
  const ordered = Boolean(sort) && sort !== SEARCH_RELEVANCE_SORT;
  // Under an explicit order the database has already put every matching row in
  // the asked-for sequence. Re-ranking by relevance on top of that would make
  // sort second in command, which is the one thing sort is not.
  const finish = (rows) => (ordered ? rows : rankCards(parsed, rows));

  // `localIds` is not a parameter of searchCatalogCards, so a tier that narrows
  // it hands over a doctored `parsed` rather than an extra argument.
  const rowsFor = (match) => cardData.searchCatalogCards(
    { ...parsed, localIds: match.localIds },
    { limit, sort, filters, words: match.words, setIds: match.setIds },
  );
  const facetsFor = (match) => (facets
    ? cardData.searchCatalogFacets(match).catch(() => null)
    : Promise.resolve(null));

  // A tier handed back to us is not re-probed. An empty result here means the
  // filter emptied it, which is a true answer and not a reason to go looking
  // in a different tier.
  if (knownMatch) {
    return { rows: finish(await rowsFor(knownMatch)), match: knownMatch, facets: null };
  }

  const setList = (parsed?.words || []).length
    ? (sets || await cardData.getSets({ language: 'en' }).catch(() => []))
    : sets;
  const tiers = matchTiers(parsed, setList);

  // With nothing ticked, a tier's own rows are a sound probe — that is what
  // shipped, and it costs no extra round trip, so the rows and the facets go
  // out together and tier one usually ends the search there and then.
  if (!hasCatalogFilters(filters)) {
    for (let i = 0; i < tiers.length; i += 1) {
      const match = tiers[i];
      const [rows, tierFacets] = i === 0
        ? await Promise.all([rowsFor(match), facetsFor(match)])
        : [await rowsFor(match), null];
      if (rows.length) {
        return {
          rows: finish(rows),
          match,
          facets: i === 0 ? tierFacets : await facetsFor(match),
        };
      }
    }
    return { rows: [], match: null, facets: null };
  }

  // With a filter on, the probe has to be the unfiltered one. So the facets go
  // first and the rows are fetched only for the tier they choose: one extra
  // round trip, paid only while something is ticked, and the reason the loading
  // line exists.
  for (let i = 0; i < tiers.length; i += 1) {
    const match = tiers[i];
    const tierFacets = await facetsFor(match);
    // A facet outage leaves no probe, so it degrades to the unfiltered rule
    // rather than abandoning the search.
    if (!tierFacets) {
      const rows = await rowsFor(match);
      if (rows.length) return { rows: finish(rows), match, facets: null };
      continue;
    }
    if (tierFacets.total > 0) {
      return { rows: finish(await rowsFor(match)), match, facets: tierFacets };
    }
  }

  return { rows: [], match: null, facets: null };
}

const smartCatalogSearch = { searchCatalog };
export default smartCatalogSearch;
